import { useCallback, useEffect, useRef, useState } from 'react'
import { ApiError } from '@services/apiClient'
import { sessionStore } from '@features/auth/services/sessionStore'
import {
  loadTrashList,
  permanentlyDeleteVaultItemById,
  restoreVaultItemById,
  type VaultFlowDeps,
  type VaultListResult,
} from '../services/vaultFlow'
import type { VaultItem } from '../types'

/**
 * ゴミ箱画面の state machine (B5、Phase 6)。
 *
 * 設計判断:
 *   - SK は `sessionStore` から取得。LOCKED / UNAUTH では即 error 状態に。
 *   - `restore` / `permanentDelete` は成否 boolean を返し、成功時は自動 reload。
 *   - reload は state を `'loading'` に戻さず、現在の items を保持したまま fetch する
 *     (削除 / 復元後のチラつき回避)。
 *   - error 文言は i18n key を返す (UI で `t()` を当てる)。
 */

export type VaultTrashState =
  | { kind: 'loading' }
  | { kind: 'ready'; items: readonly VaultItem[]; failureCount: number }
  | { kind: 'error'; messageKey: string }

const ERROR_KEYS = {
  no_session: 'vault.error.no_session',
  network: 'vault.error.network',
  generic: 'vault.error.generic',
} as const

const messageKeyFor = (err: unknown): string => {
  if (err instanceof ApiError) {
    if (err.status === 401) return ERROR_KEYS.no_session

    return ERROR_KEYS.network
  }

  return ERROR_KEYS.generic
}

export interface UseVaultTrashOptions {
  /** テスト用 DI: vaultFlow の deps を上書きする。 */
  flowDeps?: VaultFlowDeps
}

export interface UseVaultTrash {
  state: VaultTrashState
  reload: () => Promise<void>
  restore: (id: string) => Promise<boolean>
  permanentDelete: (id: string) => Promise<boolean>
}

export const useVaultTrash = (options: UseVaultTrashOptions = {}): UseVaultTrash => {
  const [state, setState] = useState<VaultTrashState>({ kind: 'loading' })
  const mountedRef = useRef(true)

  const fetchOnce = useCallback(async (): Promise<void> => {
    const session = sessionStore.getState()
    if (session.kind !== 'unlocked') {
      if (mountedRef.current) {
        setState({ kind: 'error', messageKey: ERROR_KEYS.no_session })
      }

      return
    }

    try {
      const result: VaultListResult = await loadTrashList(session.symmetricKey, options.flowDeps)
      if (!mountedRef.current) return
      setState({ kind: 'ready', items: result.items, failureCount: result.failures.length })
    } catch (err) {
      if (!mountedRef.current) return
      setState({ kind: 'error', messageKey: messageKeyFor(err) })
    }
  }, [options.flowDeps])

  useEffect(() => {
    mountedRef.current = true
    void fetchOnce()

    return () => {
      mountedRef.current = false
    }
  }, [fetchOnce])

  const restore = useCallback(
    async (id: string): Promise<boolean> => {
      try {
        await restoreVaultItemById(id, options.flowDeps)
        await fetchOnce()

        return true
      } catch {
        return false
      }
    },
    [fetchOnce, options.flowDeps],
  )

  const permanentDelete = useCallback(
    async (id: string): Promise<boolean> => {
      try {
        await permanentlyDeleteVaultItemById(id, options.flowDeps)
        await fetchOnce()

        return true
      } catch {
        return false
      }
    },
    [fetchOnce, options.flowDeps],
  )

  return { state, reload: fetchOnce, restore, permanentDelete }
}
